import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Shield } from 'lucide-react'
import { useState } from 'react'
import { PersonnelLayout } from '../components/personnel-layout'
import { Select } from '../components/ui/select'
import { TableShell } from '../components/ui/table-shell'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/org',
  component: OrgPage,
})

export const settingsOrgRoute = Route

interface OrgNode {
  id: string
  name: string
  email: string
  role: string
  roleName?: string
  supervisorId: string | null
  subordinates: OrgNode[]
}

function buildOrgTree(
  users: Array<{
    id: string
    name: string
    email: string
    role: string
    roleName?: string
    supervisorId: string | null
  }>,
): { roots: OrgNode[]; unassigned: OrgNode[] } {
  const nodeMap = new Map<string, OrgNode>()
  for (const u of users) {
    nodeMap.set(u.id, { ...u, subordinates: [] })
  }

  const roots: OrgNode[] = []
  const unassigned: OrgNode[] = []

  for (const node of nodeMap.values()) {
    if (node.supervisorId && nodeMap.has(node.supervisorId)) {
      nodeMap.get(node.supervisorId)?.subordinates.push(node)
    } else if (node.role === 'admin' || !node.supervisorId) {
      roots.push(node)
    } else {
      unassigned.push(node)
    }
  }

  // Sort: admins first, then by name
  const sortNodes = (nodes: OrgNode[]) => {
    nodes.sort((a, b) => {
      if (a.role === 'admin' && b.role !== 'admin') return -1
      if (a.role !== 'admin' && b.role === 'admin') return 1
      return a.name.localeCompare(b.name, 'de')
    })
    for (const n of nodes) sortNodes(n.subordinates)
  }
  sortNodes(roots)
  sortNodes(unassigned)

  return { roots, unassigned }
}

function OrgPage() {
  const { data: meData, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const queryClient = useQueryClient()
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: meData?.user?.role === 'admin',
  })

  const updateUser = useMutation({
    mutationFn: ({ id, supervisorId }: { id: string; supervisorId: string | null }) =>
      api.updateUser(id, { supervisorId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      notify.success('Vorgesetzter aktualisiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (isLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') return <Navigate to="/" />

  const users = (usersData?.users ?? []).filter((u: any) => !u.archived)
  const { roots, unassigned } = buildOrgTree(
    users.map((u: any) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      roleName: u.roleObj?.name ?? u.role,
      supervisorId: u.supervisorId ?? null,
    })),
  )

  return (
    <PersonnelLayout
      title="Organigramm"
      description="Vorgesetzten-Struktur und Genehmigungshierarchie"
    >
      <div className="space-y-6">
        {/* Tree view */}
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
          <h2 className="text-sm font-medium text-text-tertiary uppercase tracking-wider mb-4">
            Hierarchie
          </h2>
          {roots.length === 0 && unassigned.length === 0 ? (
            <p className="text-sm text-text-tertiary py-8 text-center">
              Keine Mitarbeiter vorhanden.
            </p>
          ) : (
            <div className="space-y-1">
              {roots.map((node) => (
                <OrgTreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  allUsers={users}
                  onChangeSupervisor={(userId, supId) =>
                    updateUser.mutate({ id: userId, supervisorId: supId })
                  }
                />
              ))}
            </div>
          )}
          {unassigned.length > 0 && (
            <div className="mt-6 pt-4 border-t border-border-subtle">
              <h3 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3">
                Ohne Zuordnung
              </h3>
              <div className="space-y-1">
                {unassigned.map((node) => (
                  <OrgTreeNode
                    key={node.id}
                    node={node}
                    depth={0}
                    allUsers={users}
                    onChangeSupervisor={(userId, supId) =>
                      updateUser.mutate({ id: userId, supervisorId: supId })
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick-assign table */}
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-border-subtle bg-bg-overlay/40">
            <h2 className="text-sm font-medium text-text-tertiary uppercase tracking-wider">
              Vorgesetzte zuweisen
            </h2>
          </div>
          <TableShell className="rounded-none border-0">
            <table className="w-full text-sm">
              <thead className="bg-bg-overlay/20">
                <tr className="text-left">
                  <th className="px-5 py-2.5 text-xs uppercase tracking-wider text-text-tertiary font-medium">
                    Mitarbeiter
                  </th>
                  <th className="px-5 py-2.5 text-xs uppercase tracking-wider text-text-tertiary font-medium">
                    Rolle
                  </th>
                  <th className="px-5 py-2.5 text-xs uppercase tracking-wider text-text-tertiary font-medium">
                    Vorgesetzter
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {users.map((u: any) => (
                  <tr key={u.id} className="hover:bg-bg-overlay/40 transition-colors">
                    <td className="px-5 py-2.5">
                      <div>
                        <p className="font-medium text-text-primary">{u.name}</p>
                        <p className="text-xs text-text-tertiary">{u.email}</p>
                      </div>
                    </td>
                    <td className="px-5 py-2.5">
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 rounded-md font-medium',
                          u.role === 'admin'
                            ? 'bg-accent/10 text-accent'
                            : 'bg-bg-overlay text-text-secondary',
                        )}
                      >
                        {u.roleObj?.name ?? u.role}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <Select
                        value={u.supervisorId ?? ''}
                        onChange={(e) =>
                          updateUser.mutate({ id: u.id, supervisorId: e.target.value || null })
                        }
                        className="h-8 w-56 text-xs"
                      >
                        <option value="">— Kein Vorgesetzter —</option>
                        {users
                          .filter((s: any) => s.id !== u.id)
                          .map((s: any) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableShell>
        </div>
      </div>
    </PersonnelLayout>
  )
}

function OrgTreeNode({
  node,
  depth,
  allUsers,
  onChangeSupervisor,
}: {
  node: OrgNode
  depth: number
  allUsers: any[]
  onChangeSupervisor: (userId: string, supervisorId: string | null) => void
}) {
  const [expanded, setExpanded] = useState(true)
  const hasSubs = node.subordinates.length > 0
  const isAdmin = node.role === 'admin'

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors hover:bg-bg-overlay/40',
          depth > 0 && 'ml-6',
        )}
      >
        {/* Expand/collapse */}
        {hasSubs ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="p-0.5 text-text-tertiary hover:text-text-primary transition-colors"
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4.5" />
        )}

        {/* Avatar placeholder */}
        <div
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-medium shrink-0',
            isAdmin ? 'bg-accent/15 text-accent' : 'bg-bg-overlay text-text-secondary',
          )}
        >
          {node.name
            .split(' ')
            .map((w) => w[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-primary truncate">{node.name}</span>
            {isAdmin && <Shield className="h-3 w-3 text-accent shrink-0" />}
            <span className="text-xs text-text-tertiary">{node.roleName}</span>
          </div>
        </div>

        {/* Subordinate count */}
        {hasSubs && (
          <span className="text-2xs text-text-tertiary bg-bg-overlay px-1.5 py-0.5 rounded-md tabular-nums">
            {node.subordinates.length} {node.subordinates.length === 1 ? 'MA' : 'MA'}
          </span>
        )}
      </div>

      {/* Children */}
      {expanded && hasSubs && (
        <div className="border-l border-border-subtle/50 ml-5">
          {node.subordinates.map((child) => (
            <OrgTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              allUsers={allUsers}
              onChangeSupervisor={onChangeSupervisor}
            />
          ))}
        </div>
      )}
    </div>
  )
}
